// CardPickup — placeholder card visual that lives on the slice floor
// and commits to RunState.pocket via PocketSystem.tryAdd when the
// player overlaps it (D-14 placeholder Container, D-11 suit colors).
//
// A `Phaser.GameObjects.Container` (NOT a Sprite, per D-14) so the
// placeholder visuals are composed from primitives — a tinted rect plus
// a 2-char rank+suit label. Phase 3 swaps the visual for a real card
// sprite, but the contract (Container with a `.card: Card` field and a
// physics body) stays put.
//
// CLAUDE.md anti-pattern note: "no magic numbers in entity files"
// targets gameplay tunables (gravity, accel, jump heights) — values
// that change game feel. The constants below (PICKUP_W/H, suit colors,
// stroke color) are visual-placeholder dimensions / palette that swap
// out wholesale at Phase 3 asset load. Keeping them as named consts at
// the top of the file is the right boundary; promoting them to
// tunables.ts would mix visual scaffolding with feel constants.

import Phaser from 'phaser';
import type { Card } from '../run/types';

// Suit colors — D-11. Hearts/diamonds red, spades/clubs off-white.
const SUIT_COLOR: Record<Card['suit'], number> = {
  h: 0xff5566,
  d: 0xff5566,
  c: 0xf5e1c8,
  s: 0xf5e1c8,
};

const PICKUP_W = 12;
const PICKUP_H = 16;
const STROKE_COLOR = 0x08050a;
const LABEL_COLOR = '#08050a';

export class CardPickup extends Phaser.GameObjects.Container {
  public readonly card: Card;

  constructor(scene: Phaser.Scene, x: number, y: number, card: Card) {
    super(scene, x, y);
    this.card = card;

    const bg = scene.add.rectangle(0, 0, PICKUP_W, PICKUP_H, SUIT_COLOR[card.suit], 1);
    bg.setStrokeStyle(1, STROKE_COLOR);

    const label = scene.add
      .text(0, 0, `${card.rank}${card.suit}`, {
        fontFamily: 'monospace',
        fontSize: '8px',
        color: LABEL_COLOR,
      })
      .setOrigin(0.5);

    this.add([bg, label]);

    // Container needs explicit size for Arcade body to inherit; default
    // is 0×0 which would no-op the physics overlap.
    this.setSize(PICKUP_W, PICKUP_H);

    scene.add.existing(this);
    scene.physics.add.existing(this);

    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setSize(PICKUP_W, PICKUP_H);
    // Container has origin 0.5 but Arcade body on a Container does not
    // auto-center — explicit offset aligns body bounds with the visual.
    body.setOffset(-PICKUP_W / 2, -PICKUP_H / 2);
    body.setAllowGravity(false);
    body.setImmovable(true);
  }
}
